import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  InputLabel,
  Chip
} from '@mui/material';
import { organizationApi } from '../../../../services/api';
import { useAlert } from '../../../../contexts/AlertContext';
import ConfirmationModal from '../../../common/ConfirmationModal';
import Modal, { Field, Label } from '../../../common/Modal';
import { FormContainer, HeaderButton } from '../../../ProjectsManagement/ProjectForm/styles';
import { Content } from '../../../ProjectsManagement/ProjectForm/styles';
import EmptyState from '../../../common/EmptyState';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { getCompanyTypeColor, getCompanyTypeTextColor } from '../../../../constants/companyTypes';
import CountrySelector from '../../../common/CountrySelector';
import CompanyTypeSelector from '../../../common/CompanyTypeSelector';

const CompaniesTab = ({ clusterId }) => {
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, company: null });

  // Unified modal state
  const [companyModal, setCompanyModal] = useState({
    open: false,
    isEdit: false,
    formData: {
      name: '',
      countryId: '',
      companyType: ''
    },
    originalId: null
  });

  useEffect(() => {
    if (clusterId) {
      fetchCompanies();
    }
  }, [clusterId]);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getCompaniesByClusterId(clusterId);
      setCompanies(data || []);
    } catch (err) {
      showAlert(err.message || 'Failed to fetch companies', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setCompanyModal(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value
      }
    }));
  };

  const resetForm = () => {
    setCompanyModal(prev => ({
      ...prev,
      formData: {
        name: '',
        countryId: '',
        companyType: ''
      }
    }));
  };

  // Unified handler for both add and edit
  const handleSaveCompany = async (e) => {
    e.preventDefault();
    
    const companyData = {
      name: companyModal.formData.name,
      countryId: companyModal.formData.countryId,
      clusterId: clusterId,
      companyType: companyModal.formData.companyType || null
    };

    try {
      setIsLoading(true);
      
      if (companyModal.isEdit) {
        // Edit existing company
        if (!companyModal.originalId) {
          showAlert('Invalid company data for update', 'error');
          return;
        }
        
        await organizationApi.updateCompany(companyModal.originalId, companyData);
        showAlert('Company updated successfully', 'success');
      } else {
        // Add new company
        await organizationApi.createCompany(companyData);
        showAlert('Company added successfully', 'success');
      }
      
      await fetchCompanies();
      // Reset modal state
      setCompanyModal({
        open: false,
        isEdit: false,
        formData: {
          name: '',
          countryId: '',
          companyType: ''
        },
        originalId: null
      });
    } catch (err) {
      const action = companyModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} company.`, 'error', err);
      console.error(`Error ${action}ing company:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCompany = (company) => {
    setDeleteModal({ open: true, company });
  };

  const confirmDelete = async () => {
    const companyToDelete = deleteModal.company;
    setDeleteModal({ open: false, company: null });
    
    if (!companyToDelete?.id) {
      showAlert('Invalid company data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteCompany(companyToDelete.id);
      await fetchCompanies();
      showAlert('Company deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting company:', err);
      showAlert(err.message || 'Failed to delete company.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCompanyForm = () => {
    return (
      <>
        <Box mb={2}>
          <TextField
            fullWidth
            name="name"
            label="Company Name"
            value={companyModal.formData.name}
            onChange={handleFormChange('name')}
            required
          />
        </Box>

        <Box mb={2}>
          <CountrySelector
            label="Country"
            value={companyModal.formData.countryId}
            onChange={(value) => setCompanyModal(prev => ({
              ...prev,
              formData: { ...prev.formData, countryId: value }
            }))}
            required
          />
        </Box>

        <Box mb={2}>
          <CompanyTypeSelector
            value={companyModal.formData.companyType}
            onChange={(value) => setCompanyModal(prev => ({
              ...prev,
              formData: { ...prev.formData, companyType: value }
            }))}
          />
        </Box>
      </>
    );
  };

  if (isLoading) {
    return (
      <FormContainer>
        <LoadingSpinner message="Loading companies..." />
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <HeaderButton onClick={() => setCompanyModal({
          open: true,
          isEdit: false,
          formData: {
            name: '',
            countryId: '',
            companyType: ''
          },
          originalId: null
        })}>
          <FaPlus /> Add Company
        </HeaderButton>
      </Box>

      <Content>
        <Grid 
          container 
          spacing={3}
          sx={{
            width: '100%',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
              xl: 'repeat(4, 1fr)'
            },
            gap: '24px',
            '& .MuiGrid-item': {
              padding: '0',
              width: '100%'
            }
          }}
        >
          {companies.length === 0 && (
            <Grid item xs={12}>
              <EmptyState
                title="No Companies Found"
                description="There are no companies in this cluster yet. Click the 'Add Company' button to add one."
              />
            </Grid>
          )}
          {companies.length > 0 && companies.map((company) => (
            <Grid item key={company.id}>
              <Card 
                sx={{ 
                  width: '100%',
                  minHeight: '140px',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s',
                  border: '1px solid #e0e0e0',
                  boxShadow: 'none',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  '&:hover': {
                    borderColor: '#bdbdbd',
                    backgroundColor: '#f1f3f5',
                  }
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '16px !important',
                  flexGrow: 1,
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: '#212529',
                      marginBottom: '8px'
                    }}
                  >
                    {company.name}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6c757d',
                        fontSize: '12px'
                      }}
                    >
                      {company.country?.name || 'No Country'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6c757d',
                        fontSize: '12px'
                      }}
                    >
                      •
                    </Typography>
                    <Chip
                      label={company.companyType || 'Not Set'}
                      size="small"
                      sx={{
                        backgroundColor: getCompanyTypeColor(company.companyType),
                        color: getCompanyTypeTextColor(company.companyType),
                        fontWeight: 500,
                        fontSize: '11px',
                        height: '20px'
                      }}
                    />
                  </Box>

                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginTop: '16px'
                  }}>
                    <Button
                      onClick={() => setCompanyModal({
                        open: true,
                        isEdit: true,
                        formData: {
                          name: company.name || '',
                          countryId: company.country?.id || '',
                          companyType: company.companyType || ''
                        },
                        originalId: company.id
                      })}
                      sx={{
                        minWidth: 'unset',
                        padding: '6px 12px',
                        fontSize: '14px',
                        color: '#0d6efd',
                        transition: 'all 0.2s ease',
                        backgroundColor: 'rgba(13, 110, 253, 0.1)',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(13, 110, 253, 0.15)',
                          color: '#0d6efd'
                        }
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteCompany(company)}
                      sx={{
                        minWidth: 'unset',
                        padding: '6px 12px',
                        fontSize: '14px',
                        color: '#dc3545',
                        transition: 'all 0.2s ease',
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(220, 53, 69, 0.15)',
                          color: '#dc3545'
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Content>

      {/* Unified modal for both adding and editing companies */}
      <Modal
        isOpen={companyModal.open}
        onClose={() => {
          setCompanyModal({
            open: false,
            isEdit: false,
            formData: {
              name: '',
              countryId: '',
              companyType: ''
            },
            originalId: null
          });
        }}
        onSubmit={handleSaveCompany}
        title={companyModal.isEdit ? "Edit Company" : "Add New Company"}
        submitText={companyModal.isEdit ? "Update Company" : "Add Company"}
        submitDisabled={!companyModal.formData.name || !companyModal.formData.countryId}
      >
        {renderCompanyForm()}
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, company: null })}
        onConfirm={confirmDelete}
        title="Delete Company"
        message={`Are you sure you want to delete ${deleteModal.company?.name}?`}
        actionButtonText="Delete"
      />
    </FormContainer>
  );
};

export default CompaniesTab; 